// Injent/glpm 0621acc8: один расчёт для экранов, сводки и проверки очагов.
import { doubleOrNil, intOrNil, form4Ochags, form5Ochags } from './mpOchags';
import { toRef, refToFullObject } from './handbookRef';
import { calculateIZdor, calculateSKS, calculateOtpadObsh, calculateOtpadTek } from '../components/forms/Form4/utils';
import { calculateDamage_0, calculateDamagePercentage } from '../components/forms/Form5/utils';

export const mobilePlotArea = (form1 = {}, dataType = 'to') => doubleOrNil(
  dataType === 'actual' ? (form1?.sga ?? form1?.s) : (form1?.lp_sga || form1?.slp)
);

export const mobileLayerStock = (tier, area) =>
  doubleOrNil(tier.zapassirga) * area + doubleOrNil(tier.zapassuh) + doubleOrNil(tier.zapaszahl);

const tiersOf = (independent) => independent?.yarusy_dependent || [];
const rowsOf = (data) => Array.isArray(data?.data) ? data.data : [];
const inTier = (row, tier) => String(row.yarus).trim() === String(tier.yarus).trim();

export const mobileStabilityClass = (sks) => !Number.isFinite(sks) || sks <= 0 ? null
  : sks < 1.5 ? 1 : sks <= 4.5 ? 2 : 3;

export const mobileLayerTotal = (formData, yarus, column) => rowsOf(formData)
  .filter(row => String(row.yarus).trim() === String(yarus).trim())
  .reduce((sum, row) => {
    let value;
    if (column === 'bpodolap') value = calculateIZdor(row);
    else if (column === 'sksporoda') value = calculateSKS(row);
    else if (column === 'ooporoda') value = calculateOtpadObsh(row);
    else if (column === 'toporoda') value = calculateOtpadTek(row);
    else if (column === 'objedola0') value = calculateDamage_0(row);
    else if (column === 'dolapovr') value = calculateDamagePercentage(row);
    else value = intOrNil(row[column]);
    return sum + value * intOrNil(row.dolap) / 10;
  }, 0);

export const mobileStandTotals = (formData, independent, area, variant = 4) => {
  const columns = variant === 4
    ? ['bpodolap', 'osldolap', 'sosldolap', 'usdolap', 'svsuhdolap', 'svvtrdolap', 'svburdolap', 'stsuhdolap', 'stvtrdolap', 'stburdolap', 'sksporoda', 'ooporoda', 'toporoda']
    : ['objedola0', 'objedola25', 'objedola50', 'objedola75', 'objedola100', 'dolapovr'];
  const tiers = tiersOf(independent);
  const weights = tiers.map(tier => mobileLayerStock(tier, area));
  const divisor = weights.reduce((sum, weight) => sum + weight, 0) || 1;
  const totals = {};
  columns.forEach(column => {
    const raw = tiers.reduce((sum, tier, index) => sum + mobileLayerTotal(formData, tier.yarus, column) * weights[index], 0) / divisor;
    totals[column] = raw.toFixed(2);
    if (column === 'sksporoda' || column === 'dolapovr' || column === 'ooporoda') totals[`${column}Raw`] = raw;
  });
  return totals;
};

export const mobileUiOchags = (variant, formData, handbooks, independent, form1Data, dataType = 'to') => {
  const form1 = { s: form1Data?.sga ?? form1Data?.s, slp: form1Data?.lp_sga || form1Data?.slp };
  const categoryFields = { catII: 'osldolap', catIII: 'sosldolap', catIV: 'usdolap', catVa: 'svsuhdolap', catVb: 'svvtrdolap', catVc: 'svburdolap', catVd: 'stsuhdolap', catVe: 'stvtrdolap', catVf: 'stburdolap' };
  const form2 = {
    useActualData: dataType === 'actual',
    plantationByTiers: tiersOf(independent).map(tier => ({
      ...tier, yarushm: tier.yarus,
      sostav: rowsOf(formData).filter(row => inTier(row, tier)).map(row => ({
        dolap: row.dolap, kodporoda: toRef(row.kodporoda, 31, handbooks),
        variant4: {
          ...Object.fromEntries(Object.entries(categoryFields).map(([key, field]) => [key, row[field]])),
          damageSigns: (formData.savedModalData?.[row.id] || []).map(sign => ({
            ...sign, prichina: toRef(sign.prichina, 10, handbooks),
          })),
        },
        variant5: {
          cat25: row.objedola25, cat50: row.objedola50, cat75: row.objedola75, cat100: row.objedola100,
          prichini: (row.causes || []).map(cause => toRef(cause, 10, handbooks)),
        },
      })),
    })),
  };
  const resolve = (ref, attr) => refToFullObject(ref, handbooks)?.[attr];
  const results = (variant === 4 ? form4Ochags : form5Ochags)(form1, form2, resolve);
  const labels = { WEAK: 'Слабая', MEDIUM: 'Средняя', STRONG: 'Сильная', SOLID: 'Сплошная' };
  return results.map(result => {
    const cause = refToFullObject(result.prichina, handbooks);
    return {
      id: cause.id, name: cause.name, type: cause.type, diseaseType: cause.disease_type,
      code: result.code, osd: result.dola, damagePercentage: result.dola,
      state: variant === 4 ? labels[result.state] : labels[result.state].toLowerCase(),
    };
  });
};

// Сводка читает локальный выбор либо сохранённую мобильную форму 6.
export const mobileSelectedFocusData = (variant, formData, form6Data, handbooks, independent, form1Data, dataType) => {
  if (Array.isArray(formData?.selectedOchags)) return formData;
  const ochags = mobileUiOchags(variant, formData, handbooks, independent, form1Data, dataType);
  const selectedCodes = (form6Data?.selectedOchagCodes || []).filter(code => ochags.some(item => String(item.code) === String(code)));
  const ochagData = {};
  const selectedOchags = selectedCodes.map((code, index) => {
    const cause = ochags.find(item => String(item.code) === String(code));
    const entry = form6Data?.vidvo?.[String(code)] || {};
    const info = { isMain: index === 0, vo: refToFullObject(entry.vidvo, handbooks)?.id ?? '',
      phase: entry.ochagPhasa || '', type: entry.ochagType || '' };
    ochagData[String(code)] = info;
    ochagData[String(cause.id)] = info;
    return String(cause.id);
  });
  return { selectedOchags, ochagData };
};
